/**
 * Administration — Specification §16.
 *
 * Offices and users in one place, because they are managed together: a user
 * cannot exist without an office, and an office cannot be archived while it
 * still has active staff.
 *
 * The API already scopes everything to the caller, so an Office Manager
 * reaching this page sees their own office and its people without the client
 * asking for anything narrower.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ROLE_KEYS, type RoleKey } from '@lto/contracts';

import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/session';
import {
  Button,
  Empty,
  ErrorState,
  Panel,
  TableSkeleton,
  formatTimestamp,
} from '../components/ui';

interface Office {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly officeType: string;
  readonly status: string;
  readonly email: string | null;
  readonly contactNo: string | null;
  readonly address: string | null;
  readonly userCount: number;
  readonly confirmationCount: number;
}

interface User {
  readonly id: string;
  readonly email: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly position: string | null;
  readonly officeId: string;
  readonly officeName: string;
  readonly role: RoleKey;
  readonly status: string;
  readonly lastLoginAt: string | null;
  readonly mustChangePassword: boolean;
}

const ROLE_LABEL: Record<RoleKey, string> = {
  ADMIN: 'Administrator',
  OFFICE_MANAGER: 'Office Manager',
  USER: 'Regular User',
  VIEWER: 'Viewer',
};

function StatusPill({ status }: { status: string }) {
  const tone =
    status === 'ACTIVE'
      ? 'var(--color-status-resolved)'
      : status === 'DISABLED'
        ? 'var(--color-warning)'
        : 'var(--color-ink-subtle)';
  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs font-medium"
      style={{ color: tone }}
    >
      <span aria-hidden="true" className="size-1.5 rounded-full" style={{ background: tone }} />
      {status.charAt(0) + status.slice(1).toLowerCase()}
    </span>
  );
}

/**
 * A temporary password, shown once.
 *
 * Held in component state and never refetched, because the server does not
 * store it in readable form — if this is dismissed the only remedy is another
 * reset, and the panel says so.
 */
function TemporaryPassword({ value, onDismiss }: { value: string; onDismiss: () => void }) {
  return (
    <div
      className="animate-rise border px-3 py-2.5"
      style={{
        borderRadius: 'var(--radius-sm)',
        background: 'var(--color-accent-subtle)',
        borderColor: 'var(--color-accent)',
      }}
      role="status"
    >
      <div className="text-sm font-medium" style={{ color: 'var(--color-accent)' }}>
        Temporary password — shown once
      </div>
      <div className="reference mt-1 text-md font-semibold">{value}</div>
      <p className="mt-1 text-xs" style={{ color: 'var(--color-ink-muted)' }}>
        Give this to the account holder. It is not stored anywhere readable, so it cannot be shown
        again — if it is lost, reset the password instead. They must change it at first sign-in.
      </p>
      <div className="mt-2">
        <Button onClick={onDismiss}>Done</Button>
      </div>
    </div>
  );
}

export function Administration() {
  const { can, user } = useSession();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'users' | 'offices'>('users');
  const [temporaryPassword, setTemporaryPassword] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const offices = useQuery({
    queryKey: ['admin', 'offices'],
    queryFn: () => api.get<Office[]>('/api/v1/offices'),
  });

  const users = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => api.get<User[]>('/api/v1/users'),
  });

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['admin'] });
    // An office added, renamed or disabled here changes who can be addressed
    // on Compose straight away, not after the directory goes stale.
    void queryClient.invalidateQueries({ queryKey: ['office-directory'] });
    void queryClient.invalidateQueries({ queryKey: ['office-assignees'] });
  }

  function onError(caught: unknown) {
    setError(
      caught instanceof ApiError
        ? (caught.problem?.detail ?? 'That action could not be completed.')
        : 'Could not reach the server.',
    );
  }

  const setUserStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.post(`/api/v1/users/${id}/status`, { status }),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError,
  });

  const changeRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: RoleKey }) =>
      api.post(`/api/v1/users/${id}/role`, { role }),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError,
  });

  const resetPassword = useMutation({
    mutationFn: (id: string) =>
      api.post<{ temporaryPassword: string }>(`/api/v1/users/${id}/reset-password`),
    onSuccess: (result) => {
      setError(null);
      setTemporaryPassword(result.temporaryPassword);
      refresh();
    },
    onError,
  });

  const createUser = useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      api.post<{ id: string; temporaryPassword: string }>('/api/v1/users', input),
    onSuccess: (result) => {
      setError(null);
      setCreating(false);
      setTemporaryPassword(result.temporaryPassword);
      refresh();
    },
    onError,
  });

  const fieldStyle = {
    borderRadius: 'var(--radius-sm)',
    borderColor: 'var(--color-rule-strong)',
    background: 'var(--color-surface)',
  } as const;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-lg font-semibold tracking-tight">Administration</h1>
        {tab === 'users' && can('user.create') && !creating && (
          <Button variant="primary" onClick={() => setCreating(true)}>
            New user
          </Button>
        )}
      </div>

      <div className="flex gap-1" role="tablist" aria-label="Administration sections">
        {(['users', 'offices'] as const).map((key) => (
          <button
            key={key}
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className="interactive border px-3 py-1.5 text-sm font-medium"
            style={{
              borderRadius: 'var(--radius-sm)',
              background: tab === key ? 'var(--color-accent-subtle)' : 'var(--color-surface)',
              borderColor: tab === key ? 'var(--color-accent)' : 'var(--color-rule-strong)',
              color: tab === key ? 'var(--color-accent)' : 'var(--color-ink-muted)',
            }}
          >
            {key === 'users' ? 'Users' : 'Offices'}
          </button>
        ))}
      </div>

      {error !== null && (
        <div
          role="alert"
          className="animate-rise border px-3 py-2 text-sm"
          style={{
            borderRadius: 'var(--radius-sm)',
            background: 'var(--color-danger-subtle)',
            borderColor: 'var(--color-danger)',
            color: 'var(--color-danger)',
          }}
        >
          {error}
        </div>
      )}

      {temporaryPassword !== null && (
        <TemporaryPassword value={temporaryPassword} onDismiss={() => setTemporaryPassword(null)} />
      )}

      {creating && (
        <Panel title="New user" description="They will receive a temporary password to change at first sign-in">
          <form
            className="grid gap-3 p-4 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              const form = new FormData(e.currentTarget);
              createUser.mutate({
                email: form.get('email'),
                firstName: form.get('firstName'),
                lastName: form.get('lastName'),
                position: form.get('position') || null,
                officeId: form.get('officeId'),
                role: form.get('role'),
              });
            }}
          >
            <label className="text-sm">
              <span className="mb-1 block font-medium">First name</span>
              <input name="firstName" required maxLength={100} className="interactive w-full border px-2.5 py-2" style={fieldStyle} />
            </label>
            <label className="text-sm">
              <span className="mb-1 block font-medium">Last name</span>
              <input name="lastName" required maxLength={100} className="interactive w-full border px-2.5 py-2" style={fieldStyle} />
            </label>
            <label className="text-sm">
              <span className="mb-1 block font-medium">Email</span>
              <input name="email" type="email" required maxLength={255} className="interactive w-full border px-2.5 py-2" style={fieldStyle} />
            </label>
            <label className="text-sm">
              <span className="mb-1 block font-medium">Position</span>
              <input name="position" maxLength={120} className="interactive w-full border px-2.5 py-2" style={fieldStyle} />
            </label>
            <label className="text-sm">
              <span className="mb-1 block font-medium">Office</span>
              <select name="officeId" required defaultValue={user?.officeId ?? ''} className="interactive w-full border px-2.5 py-2" style={fieldStyle}>
                {(offices.data ?? [])
                  .filter((o) => o.status === 'ACTIVE')
                  .map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="mb-1 block font-medium">Role</span>
              <select name="role" required defaultValue="USER" className="interactive w-full border px-2.5 py-2" style={fieldStyle}>
                {ROLE_KEYS.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABEL[r]}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex justify-end gap-2 sm:col-span-2">
              <Button type="button" variant="ghost" onClick={() => setCreating(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" loading={createUser.isPending}>
                Create user
              </Button>
            </div>
          </form>
        </Panel>
      )}

      {tab === 'users' && (
        <Panel title="Users">
          {users.isPending && <TableSkeleton rows={6} columns={6} />}
          {users.isError && <ErrorState message="Could not load users." onRetry={() => void users.refetch()} />}
          {users.isSuccess && users.data.length === 0 && <Empty title="No users to show" />}
          {users.isSuccess && users.data.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left">
                <thead className="sticky-head">
                  <tr className="border-b text-xs font-medium uppercase tracking-wider" style={{ borderColor: 'var(--color-rule)', color: 'var(--color-ink-subtle)' }}>
                    <th scope="col" className="px-4 py-2">Name</th>
                    <th scope="col" className="px-4 py-2">Office</th>
                    <th scope="col" className="px-4 py-2">Role</th>
                    <th scope="col" className="px-4 py-2">Status</th>
                    <th scope="col" className="px-4 py-2">Last sign-in</th>
                    <th scope="col" className="px-4 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="stagger">
                  {users.data.map((u) => {
                    const self = u.id === user?.id;
                    return (
                      <tr key={u.id} className="row-hover border-b last:border-b-0" style={{ borderColor: 'var(--color-rule)' }}>
                        <td className="px-4 py-2">
                          <div className="font-medium">
                            {u.firstName} {u.lastName}
                            {self && <span className="ml-2 text-xs" style={{ color: 'var(--color-accent)' }}>You</span>}
                          </div>
                          <div className="text-xs" style={{ color: 'var(--color-ink-subtle)' }}>{u.email}</div>
                        </td>
                        <td className="px-4 py-2" style={{ color: 'var(--color-ink-muted)' }}>{u.officeName}</td>
                        <td className="px-4 py-2">
                          {can('user.change_role') && !self ? (
                            <select
                              value={u.role}
                              onChange={(e) => changeRole.mutate({ id: u.id, role: e.target.value as RoleKey })}
                              className="interactive border px-1.5 py-1 text-sm"
                              style={fieldStyle}
                            >
                              {ROLE_KEYS.map((r) => (
                                <option key={r} value={r}>{ROLE_LABEL[r]}</option>
                              ))}
                            </select>
                          ) : (
                            <span style={{ color: 'var(--color-ink-muted)' }}>{ROLE_LABEL[u.role]}</span>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <StatusPill status={u.status} />
                          {u.mustChangePassword && (
                            <div className="text-xs" style={{ color: 'var(--color-warning)' }}>Must change password</div>
                          )}
                        </td>
                        <td className="tabular px-4 py-2 text-xs" style={{ color: 'var(--color-ink-subtle)' }}>
                          {u.lastLoginAt === null ? 'Never' : formatTimestamp(u.lastLoginAt)}
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex justify-end gap-1.5">
                            {can('user.reset_password') && (
                              <Button onClick={() => resetPassword.mutate(u.id)} loading={resetPassword.isPending}>
                                Reset password
                              </Button>
                            )}
                            {can('user.disable') && !self && (
                              <Button
                                onClick={() =>
                                  setUserStatus.mutate({
                                    id: u.id,
                                    status: u.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE',
                                  })
                                }
                              >
                                {u.status === 'ACTIVE' ? 'Disable' : 'Reactivate'}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}

      {tab === 'offices' && (
        <Panel title="Offices">
          {offices.isPending && <TableSkeleton rows={4} columns={5} />}
          {offices.isError && <ErrorState message="Could not load offices." onRetry={() => void offices.refetch()} />}
          {offices.isSuccess && offices.data.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left">
                <thead className="sticky-head">
                  <tr className="border-b text-xs font-medium uppercase tracking-wider" style={{ borderColor: 'var(--color-rule)', color: 'var(--color-ink-subtle)' }}>
                    <th scope="col" className="px-4 py-2">Office</th>
                    <th scope="col" className="px-4 py-2">Contact</th>
                    <th scope="col" className="px-4 py-2">Status</th>
                    <th scope="col" className="px-4 py-2 text-right">Users</th>
                    <th scope="col" className="px-4 py-2 text-right">Confirmations</th>
                  </tr>
                </thead>
                <tbody className="stagger">
                  {offices.data.map((o) => (
                    <tr key={o.id} className="row-hover border-b last:border-b-0" style={{ borderColor: 'var(--color-rule)' }}>
                      <td className="px-4 py-2">
                        <div className="font-medium">{o.name}</div>
                        <div className="reference text-xs" style={{ color: 'var(--color-ink-subtle)' }}>{o.code}</div>
                      </td>
                      <td className="px-4 py-2 text-xs" style={{ color: 'var(--color-ink-muted)' }}>
                        <div>{o.email ?? <span style={{ color: 'var(--color-ink-subtle)' }}>No email recorded</span>}</div>
                        <div className="tabular">{o.contactNo ?? <span style={{ color: 'var(--color-ink-subtle)' }}>No phone recorded</span>}</div>
                      </td>
                      <td className="px-4 py-2"><StatusPill status={o.status} /></td>
                      <td className="tabular px-4 py-2 text-right">{o.userCount}</td>
                      <td className="tabular px-4 py-2 text-right">{o.confirmationCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}
    </div>
  );
}
